/* eslint-disable react/prop-types */

import { EventType, InteractionStatus, InteractionType } from '@azure/msal-browser'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react'
import { Box, CssBaseline, Toolbar, Typography } from '@mui/material'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { ThemeOptions, ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { Route } from 'react-router'
import { Navigate, Routes } from 'react-router-dom'
import './App.css'
import { AuthorizedAxios } from './app/axios/logikspark'
import { useAppDispatch, useAppSelector } from './app/hooks'
import logger from './app/middleware/log'
import { usePageTitle } from './app/hooks/usePageTitle'
import { getCompanyName } from './app/utils'

import './app/dayjs'
import useNavigation from './app/hooks/useNavigation'
import { AppBar } from './components/AppBar/AppBar'
import AppBarDetail from './components/AppBarDetail/AppBarDetail'
import { NavbarContext } from './components/AppBarDetail/NavbarContext'
import LocationBasedBreadcrumbs from './components/Breadcrumb/LocationBasedBreadcrumbs'
import { SubCategoryItem } from './components/Buttons/SubCategoryItem'
import { ConfirmDialogProvider } from './components/ConfirmDialog'
import { Drawer, DrawerHeader } from './components/Drawer/Drawer'
import Localization from './components/Localization/Localization'
import Logo from './components/Logo/Logo'
import NoMatch from './components/NoMatch/NoMatch'
import NotImplemented from './components/NoMatch/NotImplemented'
import TenantRoute from './components/Routes/TenantRoute'
import rootEnum from './components/Routes/rootEnum'
import MutationSpinner from './components/Spinner/MutationSpinner'
import SpinnerBlock from './components/Spinner/SpinnerBlock'
import { ThemeContext, ThemeContextState } from './components/Theme/Theme'
import Assets from './features/Assets/Assets'
import CarrierTable from './features/Carrier/CarrierTable'
import Office365Configuration from './features/CommonSettings/Office365/Office365'
import RoleTable from './features/CommonSettings/RoleManagement/RoleTable'
import VerifiedDomainsTable from './features/CommonSettings/VerifiedDomains/VerifiedDomainsTable'
import FederationProvidersTable from './features/CommonSettings/FederationProviders/FederationProvidersTable'
import ImpersonationGrantsPage from './features/CommonSettings/Impersonation/ImpersonationGrantsPage'
import CurrencyTable from './features/Currency/CurrencyTable'
import CustomerTable from './features/CustomerSettings/Customer/CustomerTable'
import Dashboard from './features/Dashboard/Dashboard'
import DocumentTypeTable from './features/DocumentType/DocumentTypeTable'
import Journey from './features/Journey/Journey'
import JourneyPlannerPage from './features/Journey/Planner/JourneyPlannerPage'
import LoadTemplateTable from './features/LoadTemplate/LoadTemplateTable'
import Locations from './features/Locations/Locations'
import Note from './features/Notes/Note'
import AssetHireFormContainer from './features/Orders/AssetHire/AssetHireFormContainer'
import AssetHireTable from './features/Orders/AssetHire/AssetHireTable/AssetHireTable'
import ViewAssetHire from './features/Orders/AssetHire/ViewAssetHire'
import ShipmentFormContainer from './features/Orders/NewShipment/ShipmentFormContainer'
import ShipmentTable from './features/Orders/NewShipment/ShipmentTable/ShipmentTable'
import ViewShipmentContainer from './features/Orders/NewShipment/ViewShipmentContainer'
import ProductTabs from './features/Products/ProductsTab'
import ServiceTable from './features/Service/ServiceTable'
import ContactTypeTab from './features/Settings/Contacts/ContactType/ContactTypeTab'
import CreateAsset from './features/TenantSettings/Asset/CreateAsset'
import AssetSettings from './features/TenantSettings/AssetSettings/AssetSettings'
import BrandingConfiguration from './features/TenantSettings/Components/BrandingConfiguration'
import ShippingTabs from './features/TenantSettings/Shipping/ShippingTabs'
import { fetchAllBrandingSelector } from './features/TenantSettings/selectors'
import { fetchBrandingThunk } from './features/TenantSettings/slice'
import { IBranding } from './features/TenantSettings/types'
import CreateTransport from './features/Transport/components/CreateTransport/CreateTransport'
import UnassignedRoutes from './features/Transport/components/Routes/UnassignedRoutes'
import TransportJobTable from './features/TransportJob/TransportJobTable'
import UserManagementTab from './features/UserManagement/Components/UserManagementTab/UserManagementTab'
import WorkforceTable from './features/Workforce/WorkforceTable'
import WorkforceTypeTable from './features/WorkforceType/WorkforceTypeTable'
import { fetchLoggedInUserSelector, isSignInSelector } from './features/selectors'
import { fetchLoggedInUserThunk, isSigninUserActions } from './features/slice'
import { ILoggedInUser } from './features/types'
import { Anchor } from './types'

function App(): ReactElement<any, any> {
  usePageTitle()
  const { instance, inProgress } = useMsal()

  const dispatch = useAppDispatch()
  const isSignIn = useSelector(isSignInSelector)
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const isLoggedInUserFetching = useAppSelector(fetchLoggedInUserSelector.isFetching)
  const isLoggedInUserLoaded = useAppSelector(fetchLoggedInUserSelector.isFinished)
  const loggedInUserError = useAppSelector(fetchLoggedInUserSelector.error)

  const { selectedCategory, selectedSubCategory, categories, appBarOpen } = useNavigation()

  // E8 F8-1 cutover: the implicit useMsalAuthentication auto-redirect is gone — AppShell now routes an
  // unauthenticated user to the explicit /login page. This effect is the sign-in bootstrap that the
  // auto-redirect's silent acquisition used to provide: when MSAL has an account (a fresh redirect handled
  // by AuthCallbackPage, OR a cached account after a page reload) but the redux sign-in flag is not yet set,
  // mark signed-in (ensuring an active account first) so the /user/me fetch and the rest of the app proceed.
  const isAuthenticated = useIsAuthenticated()
  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None || isSignIn.isSignIn) {
      return
    }
    if (!instance.getActiveAccount()) {
      const account = instance.getAllAccounts()[0]
      if (account) {
        instance.setActiveAccount(account)
      }
    }
    dispatch(isSigninUserActions.signIn())
  }, [isAuthenticated, inProgress, isSignIn.isSignIn, instance, dispatch])

  // Log MSAL state changes for debugging
  useEffect(() => {
    logger.info('MSAL state - inProgress:', inProgress, 'isSignedIn:', isSignIn.isSignIn)
    console.log('MSAL state - inProgress:', inProgress, 'isSignedIn:', isSignIn.isSignIn)
  }, [inProgress, isSignIn.isSignIn])

  const [themeContextState, setThemeContextState] = useState(new ThemeContextState())
  themeContextState.setThemeContextState = setThemeContextState

  const [readyState, setReadyState] = useState(false)

  const brandingData = useAppSelector(fetchAllBrandingSelector.data)
  const brandingError = useAppSelector(fetchAllBrandingSelector.error)

  const branding = useMemo<IBranding | null>(() => {
    if (!brandingData) return null
    return brandingData
  }, [brandingData])

  useEffect(() => {
    if (!brandingError) return
    setReadyState(true)
  }, [brandingError])

  useEffect(() => {
    if (!loggedInUserError) return
    setReadyState(true)
  }, [loggedInUserError])

  useEffect(() => {
    if (!branding) return

    const workingThemeSource = buildThemeSource(branding)
    themeContextState.createAndSetTheme(workingThemeSource)

    setReadyState(true)
  }, [branding])

  useEffect(() => {
    if (loggedInUser?.tenantId) dispatch(fetchBrandingThunk(loggedInUser?.tenantId))
  }, [loggedInUser])

  useEffect(() => {
    if (isSignIn.isSignIn) {
      const selectedCustomerTenant = localStorage.getItem('selectedCustomerTenant')
      if (selectedCustomerTenant) {
        if (selectedCustomerTenant.includes('customer-')) {
          const customerId = selectedCustomerTenant.split('-')[1]
          dispatch(fetchLoggedInUserThunk({ customerId: +customerId }))
        } else {
          const tenantId = selectedCustomerTenant.split('-')[1]
          dispatch(fetchLoggedInUserThunk({ tenantId: +tenantId }))
        }
      } else {
        dispatch(fetchLoggedInUserThunk({}))
      }
    }
  }, [isSignIn.isSignIn])

  useEffect(() => {
    const callbackId = instance.addEventCallback((event: any) => {
      logger.info('Event type', event.eventType)
      // msal-browser v5 removed LOGIN_START — an interactive redirect login now
      // surfaces as ACQUIRE_TOKEN_START with interactionType=Redirect. Silent
      // refreshes (no interaction) must NOT clear the selected context.
      if (
        (event.eventType === EventType.ACQUIRE_TOKEN_START && event.interactionType === InteractionType.Redirect) ||
        event.eventType === EventType.LOGOUT_START
      ) {
        localStorage.removeItem('selectedCustomerTenant')
      }
      // Sign-in detection + deep-link restore + login-failure handling now live on the dedicated
      // /auth/callback route: the interactive redirect completes there and AuthCallbackPage owns
      // ACQUIRE_TOKEN_SUCCESS/FAILURE (App is not mounted during callback processing). The sign-in bootstrap
      // effect above covers a cached account on reload. So this callback only clears the selected context on
      // a new interactive login/logout (above) and resets the sign-in flag on logout completion (below).
      if (event.eventType === EventType.LOGOUT_END && event?.payload) {
        dispatch(isSigninUserActions.signOut())
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [instance, dispatch])

  void AuthorizedAxios()

  const [drawerPosition, setDrawerPosition] = useState('left')

  const setMenuPosition = (menuPosition: string) => {
    menuPosition === 'right' ? setDrawerPosition('right') : setDrawerPosition('left')
  }

  const isSignedIn = isSignIn.isSignIn
  const isUserLoadComplete = isLoggedInUserLoaded || loggedInUserError
  const isAuthInProgress = inProgress !== InteractionStatus.None

  // AppShell only renders App once MSAL reports an authenticated account (otherwise it routes to /login),
  // so reaching here not-yet-signed-in means the sign-in bootstrap effect above is mid-flight (or a token
  // interaction is in progress) — show the spinner until the redux sign-in flag flips, never an error.
  if (!isSignedIn) {
    return <SpinnerBlock />
  }

  if (
    isSignedIn &&
    (!readyState || isAuthInProgress || isLoggedInUserFetching || !isUserLoadComplete)
  ) {
    return <SpinnerBlock />
  }
  // eslint-disable-next-line
  const list = () => (
    <List>
      {categories.map((category) => (
        <ListItem
          key={category.name}
          disablePadding
          onClick={() => {
            category.action()
          }}
        >
          {category.element}
        </ListItem>
      ))}
    </List>
  )

  return (
    <NavbarContext.Provider value={{ setMenuPosition }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Localization>
          <ThemeContext.Provider value={themeContextState}>
            <ThemeContext.Consumer>
              {(value) => (
                <ThemeProvider theme={value.theme}>
                  <ConfirmDialogProvider>
                    <MutationSpinner />
                    <Box sx={{ display: 'flex' }}>
                      <CssBaseline />
                      <AppBar position='fixed' open={appBarOpen} anchor={drawerPosition as Anchor}>
                        <Toolbar>
                          <Box display='flex' flexGrow={1}>
                            <LocationBasedBreadcrumbs />
                          </Box>
                          <AppBarDetail />
                        </Toolbar>
                      </AppBar>
                      <Drawer
                        variant='permanent'
                        open={appBarOpen}
                        anchor={drawerPosition as Anchor}
                      >
                        <DrawerHeader sx={{ padding: 0 }}>
                          <Logo
                            companyName={getCompanyName(loggedInUser) ?? 'LogikSpark'}
                            isRtl={drawerPosition === 'right'}
                            open={appBarOpen}
                          />
                        </DrawerHeader>
                        <Box display='flex' sx={{ overflow: 'hidden' }}>
                          <Box flex='0 0 auto'>{list()}</Box>
                          <Box flex='1' sx={{ overflow: 'hidden' }}>
                            <Box
                              padding={0}
                              bgcolor='white'
                              style={{ height: 'calc(100vh - 64px)' }}
                              sx={{ overflow: 'hidden' }}
                            >
                              <Typography
                                variant='h6'
                                sx={{
                                  color: themeContextState.theme.palette.primary.main,
                                  fontWeight: 700,
                                  ...(drawerPosition === 'left' && { marginLeft: '16px' }),
                                  ...(drawerPosition === 'right' && { marginRight: '48px' }),
                                  paddingTop: '16px',
                                  height: '56px',
                                }}
                              >
                                {categories.find((c) => c.name === selectedCategory)?.name ?? ''}
                              </Typography>
                              <List disablePadding>
                                {categories
                                  .find((c) => c.name === selectedCategory)
                                  ?.items.map((item, index) => (
                                    <SubCategoryItem
                                      key={index}
                                      label={item.name}
                                      action={item.action}
                                      isSelected={selectedSubCategory === item.name}
                                      selectedPath={selectedSubCategory || ''}
                                      items={item.items}
                                    />
                                  ))}
                              </List>
                            </Box>
                          </Box>
                        </Box>
                      </Drawer>
                      <Box
                        component='main'
                        sx={{ flexGrow: 1, p: '20px', width: appBarOpen ? '70%' : '90%' }}
                      >
                        <AuthenticatedTemplate>
                          <DrawerHeader />
                          <Routes>
                            <Route
                              path={rootEnum.ROOT}
                              element={<Navigate replace to='/dashboard' />}
                            />
                            {/* E8 F8-5: cross-subdomain switch lands here; PersonaGate (AppShell) has
                                already resolved ?personaId by the time App renders, so redirect on. */}
                            <Route
                              path={rootEnum.PERSONA_SPLASH}
                              element={<Navigate replace to='/dashboard' />}
                            />
                            <Route path={rootEnum.DASHBOARD} element={<Dashboard />} />
                            <Route path={rootEnum.SHIPMENTS} element={<ShipmentTable />} />
                            <Route path={rootEnum.ASSET_HIRES} element={<AssetHireTable />} />
                            <Route
                              path={rootEnum.CREATE_ASSET_HIRE}
                              element={<AssetHireFormContainer />}
                            />
                            <Route path={rootEnum.VIEW_ASSET_HIRE} element={<ViewAssetHire />} />
                            <Route
                              path={rootEnum.EDIT_ASSET_HIRE}
                              element={<AssetHireFormContainer />}
                            />
                            <Route path={rootEnum.JOURNEY} element={<Journey />} />
                            <Route path={rootEnum.JOURNEY_PLANNER} element={<JourneyPlannerPage />} />
                            <Route
                              path={rootEnum.VIEW_SHIPMENT}
                              element={<ViewShipmentContainer />}
                            />
                            <Route
                              path={rootEnum.EDIT_SHIPMENT}
                              element={<ShipmentFormContainer />}
                            />
                            <Route path={rootEnum.LOCATIONS} element={<Locations />} />
                            <Route path={rootEnum.CREATE_ASSET} element={<CreateAsset />} />
                            <Route
                              path={rootEnum.INTEGRATIONS}
                              element={<Office365Configuration />}
                            />
                            <Route
                              path={rootEnum.USER_MANAGEMENT}
                              element={<UserManagementTab />}
                            />
                            <Route path={rootEnum.SHIPPING} element={<ShippingTabs />} />
                            <Route path={rootEnum.ROLES_PERMISSIONS} element={<RoleTable />} />
                            <Route path={rootEnum.ASSET_MANAGEMENT} element={<AssetSettings />} />
                            <Route path={rootEnum.BRANDING} element={<BrandingConfiguration />} />
                            <Route
                              path={rootEnum.VERIFIED_DOMAINS}
                              element={<VerifiedDomainsTable />}
                            />
                            <Route
                              path={rootEnum.FEDERATION}
                              element={
                                <TenantRoute>
                                  <FederationProvidersTable />
                                </TenantRoute>
                              }
                            />
                            {/* E11 R1 (WI 611): GA-only grants page. The guard is in-page (useMe), so no
                                TenantRoute wrapper — a non-GA gets the Forbidden panel and no API call. */}
                            <Route
                              path={rootEnum.IMPERSONATION}
                              element={<ImpersonationGrantsPage />}
                            />
                            <Route path={rootEnum.WORKFORCE} element={<WorkforceTable />} />
                            <Route
                              path={rootEnum.UNASSIGNED_ROUTES}
                              element={<UnassignedRoutes />}
                            />
                            <Route path={rootEnum.ASSETS} element={<Assets />} />
                            <Route path={rootEnum.PRODUCTS} element={<ProductTabs />} />
                            <Route path={rootEnum.CURRENCY_SETTINGS} element={<CurrencyTable />} />
                            <Route path={rootEnum.CONTACTS_SETTINGS} element={<ContactTypeTab />} />
                            <Route
                              path={rootEnum.WORKFORCE_TYPE}
                              element={<WorkforceTypeTable />}
                            />
                            <Route path={rootEnum.CARRIERS} element={<CarrierTable />} />
                            <Route path={rootEnum.SERVICES} element={<ServiceTable />} />
                            <Route path={rootEnum.DOCUMENT_TYPE} element={<DocumentTypeTable />} />
                            <Route path={'/notes'} element={<Note />} />
                            <Route
                              path={rootEnum.SHIPMENT_STEPPER}
                              element={<ShipmentFormContainer />}
                            />
                            <Route
                              path={rootEnum.CUSTOMERS}
                              element={
                                <TenantRoute>
                                  <CustomerTable />
                                </TenantRoute>
                              }
                            />
                            <Route
                              path={rootEnum.CREATE_TRANSPORT}
                              element={
                                <TenantRoute>
                                  <CreateTransport />
                                </TenantRoute>
                              }
                            />
                            <Route
                              path={rootEnum.TRANSPORT_JOB_LISTS}
                              element={
                                <TenantRoute>
                                  <TransportJobTable />
                                </TenantRoute>
                              }
                            />
                            <Route
                              path={`${rootEnum.LOAD_TEMPLATES}`}
                              element={<LoadTemplateTable />}
                            />
                            <Route path={`${rootEnum.ASSET_HIRES}`} element={<AssetHireTable />} />
                            <Route path={rootEnum.COMING_SOON} element={<NotImplemented />} />
                            <Route path='*' element={<NoMatch />} />
                          </Routes>
                        </AuthenticatedTemplate>
                        <UnauthenticatedTemplate>
                          {/* If MSAL drops to unauthenticated while App is mounted (e.g. token/session
                              expiry mid-session), send the user to the explicit login page — consistent
                              with AppShell's unauthenticated guard. */}
                          <Navigate to={rootEnum.LOGIN} replace />
                        </UnauthenticatedTemplate>
                      </Box>
                    </Box>
                  </ConfirmDialogProvider>
                </ThemeProvider>
              )}
            </ThemeContext.Consumer>
          </ThemeContext.Provider>
        </Localization>
      </LocalizationProvider>
    </NavbarContext.Provider>
  )
}

export default App

function buildThemeSource(branding: IBranding) {
  const { contrastTextColor, primaryPalletColor, secondaryPalletColor, textColor } = branding

  const themeSource: ThemeOptions = {
    palette: {
      primary: {
        contrastText: `#${contrastTextColor}`,
        main: `#${primaryPalletColor}`,
      },
      secondary: {
        main: `#${secondaryPalletColor}`,
      },
      text: {
        primary: `#${textColor}`,
      },
      accordion: {
        border: {
          radius: '8px',
        },
        parent: {
          bg: '#F6F6F6BF',
        },
      },
    },
    typography: {
      fontFamily: 'Roboto, sans-serif',
    },
    components: {
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: '#636363',
            '&.active': { fontWeight: 'bold' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `#${primaryPalletColor}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `#${primaryPalletColor}`,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              color: `#${textColor}`,
              opacity: 0.38,
            },
          },
        },
      },
    },
    transitions: {
      duration: {
        enteringScreen: 300,
        leavingScreen: 300,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
  }
  return themeSource
}
